'use client';
import { ChangeEvent, useState } from 'react';

import { useForm } from '@/hooks/useForm';

import { cmsApiEndpoint } from '@/constant/env';
import { EMAIL_REGEX } from '@/utils/validation';
import { apiCall } from '@/utils/fetch';
import { useTranslation } from '@/lib/i18n/i18n.client';

import { CaptureLeadPopupProps } from './LeadPopUpModal';
import { apiEndpoints } from '@/constant/urls';

export const MULTIPLE_INPUTS = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  message: 'message',
  websiteUrl: 'websiteUrl',
  companyName: 'companyName',
  companyEmail: 'companyEmail',
  useCase: 'useCase',
  accelerator: 'accelerator',
} as const;

type LeadPopUpFormState = {
  [key in keyof typeof MULTIPLE_INPUTS]: string;
};

type UseLeadPopUpFormOptions = {
  onSuccess?: () => void;
  onError?: () => void;
};

const isInputRequired = (
  multipleInputs: CaptureLeadPopupProps['multipleInputs'],
  validation: string
) => multipleInputs?.some((input) => input.fieldMapping === validation);

const useLeadPopUpForm = (
  props: CaptureLeadPopupProps,
  options?: UseLeadPopUpFormOptions
) => {
  const { texts } = useTranslation();

  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const form = useForm<LeadPopUpFormState>({
    initialValues: {
      email: '',
      firstName: '',
      lastName: '',
      message: '',
      websiteUrl: '',
      useCase: '',
      accelerator: '',
      companyName: '',
      companyEmail: '',
    },
    validateOnChange: false,
    validateOnBlur: false,
    validator: {
      email: (value) => {
        if (!isInputRequired(props.multipleInputs, MULTIPLE_INPUTS.email))
          return;
        if (!value) return texts.submission_status_message.required;
        if (typeof value !== 'string' || !EMAIL_REGEX.test(value)) {
          return texts.gated_content.please_provide_a_valid_email_address;
        }
      },
      companyEmail: (value) => {
        if (
          !isInputRequired(props.multipleInputs, MULTIPLE_INPUTS.companyEmail)
        )
          return;
        if (!value) return texts.submission_status_message.required;
        if (typeof value !== 'string' || !EMAIL_REGEX.test(value)) {
          return texts.gated_content.please_provide_a_valid_email_address;
        }
      },
      companyName: (value) => {
        if (
          isInputRequired(props.multipleInputs, MULTIPLE_INPUTS.companyName) &&
          !value
        )
          return texts.submission_status_message.required;
      },
      firstName: (value) => {
        if (
          isInputRequired(props.multipleInputs, MULTIPLE_INPUTS.firstName) &&
          !value
        )
          return texts.submission_status_message.required;
      },
      accelerator: (value) => {
        if (props.dropdownOptions?.dropdownOptions && !value)
          return texts.submission_status_message.required;
      },
      useCase: (value) => {
        if (
          isInputRequired(props.multipleInputs, MULTIPLE_INPUTS.useCase) &&
          !value
        )
          return texts.submission_status_message.required;
      },
      message: (value) => {
        if (
          isInputRequired(props.multipleInputs, MULTIPLE_INPUTS.message) &&
          !value
        )
          return texts.submission_status_message.required;
      },
      websiteUrl: (value) => {
        if (
          isInputRequired(props.multipleInputs, MULTIPLE_INPUTS.websiteUrl) &&
          !value
        )
          return texts.submission_status_message.required;
      },
    },
  });

  const { handleSubmit: onFormSubmit, setFieldValue, values } = form;

  const buildFormData = () => {
    const formData: Record<string, string> = {};

    props.multipleInputs?.forEach((input) => {
      formData[input.fieldMapping] =
        values[input.fieldMapping as keyof LeadPopUpFormState] || '';
    });

    return formData;
  };

  const handleSubmit = async (): Promise<'success' | 'invalid' | 'error'> => {
    setApiError(null);
    setIsLoading(true);

    const emailRequired = isInputRequired(
      props.multipleInputs,
      MULTIPLE_INPUTS.email
    );
    const companyEmailRequired = isInputRequired(
      props.multipleInputs,
      MULTIPLE_INPUTS.companyEmail
    );

    if (
      emailRequired &&
      (!values.email ||
        typeof values.email !== 'string' ||
        !EMAIL_REGEX.test(values.email))
    ) {
      setIsLoading(false);
      return 'invalid';
    }

    if (
      companyEmailRequired &&
      (!values.companyEmail ||
        typeof values.companyEmail !== 'string' ||
        !EMAIL_REGEX.test(values.companyEmail))
    ) {
      setIsLoading(false);
      return 'invalid';
    }

    try {
      let response: any;

      const leadsCollection = props.leadsCollection ?? 'ActiveCampaign';

      if (leadsCollection === 'ActiveCampaign') {
        response = await apiCall({
          url: cmsApiEndpoint + '/users/capture_details',
          method: 'POST',
          body: {
            ...buildFormData(),
            activeCampaignListId: props.activeCampaignListId,
            saveExistingEmails: props.saveExistingEmails,
          },
        });
      } else if (leadsCollection === 'SalesForce') {
        response = await apiCall({
          url: apiEndpoints.contactSales,
          method: 'POST',
          body: {
            ...buildFormData(),
            pageUrl: window.location.href.split('#')[0],
          },
        });
      }
      setIsLoading(false);

      if (!response || response?.error) {
        const errorMessage =
          response?.error || texts.gated_content.default_error_message;
        setApiError(errorMessage);
        options?.onError?.();

        if (
          typeof errorMessage === 'string' &&
          errorMessage.toLowerCase().includes('email')
        ) {
          return 'invalid';
        }

        return 'error';
      }

      options?.onSuccess?.();
      return 'success';
    } catch (error: any) {
      setIsLoading(false);
      setApiError(error?.message || texts.gated_content.default_error_message);
      console.error(error);
      options?.onError?.();
      return 'error';
    }
  };

  const getSetFieldValue =
    (name: keyof LeadPopUpFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFieldValue(name, e.target.value);

  return {
    ...form,
    errors: {
      email: form.errors?.email || apiError || undefined,
      firstName: form.errors?.firstName,
      lastName: form.errors?.lastName,
      message: form.errors?.message,
      websiteUrl: form.errors?.websiteUrl,
      useCase: form.errors?.useCase,
      accelerator: form.errors?.accelerator,
      companyName: form.errors?.companyName,
      companyEmail: form.errors?.companyEmail,
    },
    isLoading,
    wasSubmitted: form.hasSubmitted,
    handleSubmit,
    getSetFieldValue,
    onFormSubmit,
  };
};

export default useLeadPopUpForm;
